import { useState, useEffect, useRef } from "react";
import { Building2, User, Landmark, ShieldCheck, Camera, Loader2, Plane } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HostVerificationCard } from "@/components/host/HostVerificationCard";

export function HostSettings() {
  const { user, refresh } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState("");
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [ach, setAch] = useState({ holder: "", routing: "", account: "", type: "checking" });
  const [photo, setPhoto] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInput = useRef(null);

  useEffect(() => {
    api.get("/host/me/settings").then((r) => {
      setS(r.data);
      setName(r.data.name || user?.name || "");
      setEmail(r.data.host_email || user?.email || "");
      setPhoto(r.data.profile_photo || "");
      setAch({ holder: r.data.bank_account_holder || "", routing: "", account: "", type: r.data.bank_account_type || "checking" });
    }).catch(() => {});
  }, [user]);

  if (!s) return <p className="text-slate-400">Loading…</p>;

  const uploadPhoto = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/host/me/settings", { profile_photo: r.data.url });
      setPhoto(r.data.url);
      toast.success("Profile photo updated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setPhotoUploading(false); }
  };

  const saveType = async () => {
    setSaving("type");
    try {
      await api.put("/host/me/settings", {
        host_type: s.host_type,
        business_name: s.host_type === "business" ? s.business_name : "",
        ein: s.host_type === "business" ? s.ein : "",
        host_phone: s.host_phone,
      });
      toast.success("Settings saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  const saveName = async () => {
    setSaving("name");
    try { await api.put("/auth/profile", { name }); await refresh(); toast.success("Name updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(""); }
  };
  const saveEmail = async () => {
    setSaving("email");
    try { await api.post("/auth/change-email", { new_email: email, current_password: emailPwd }); await refresh(); setEmailPwd(""); toast.success("Email updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(""); }
  };
  const savePwd = async () => {
    setSaving("pwd");
    try { await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd }); setCurPwd(""); setNewPwd(""); toast.success("Password updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(""); }
  };
  const saveAch = async () => {
    setSaving("ach");
    try {
      await api.put("/host/me/settings", {
        bank_account_holder: ach.holder,
        bank_routing: ach.routing || undefined,
        bank_account_number: ach.account || undefined,
        bank_account_type: ach.type,
      });
      const r = await api.get("/host/me/settings");
      setS(r.data);
      setAch({ holder: r.data.bank_account_holder || "", routing: "", account: "", type: r.data.bank_account_type || "checking" });
      toast.success("Payout details saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };
  const saveAirport = async () => {
    setSaving("airport");
    try {
      await api.put("/host/me/settings", {
        offers_airport_service: !!s.offers_airport_service,
        airport_fee: s.offers_airport_service ? Number(s.airport_fee || 0) : 0,
      });
      toast.success("Airport service settings saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  const TypeCard = ({ value, icon: Icon, title, desc }) => (
    <button onClick={() => set("host_type", value)} data-testid={`host-type-${value}`}
      className={`flex-1 text-left rounded-2xl border-2 p-5 transition-colors ${s.host_type === value ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
      <span className={`grid place-items-center w-10 h-10 rounded-xl ${s.host_type === value ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><Icon size={18} /></span>
      <p className="font-heading font-semibold text-slate-900 mt-3">{title}</p>
      <p className="text-sm text-slate-500 mt-1">{desc}</p>
    </button>
  );

  return (
    <div className="max-w-2xl space-y-6" data-testid="host-settings">
      {/* Public profile photo */}
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><Camera size={18} className="text-blue-600" /> Profile photo</h3>
        <p className="text-sm text-slate-500 mb-5">Shown to guests on your vehicle listings. A friendly photo builds trust and boosts bookings.</p>
        <div className="flex items-center gap-5">
          <div className="relative">
            {photo ? (
              <img src={photo} alt="Profile" data-testid="host-photo-preview" className="w-24 h-24 rounded-full object-cover border border-slate-200" />
            ) : (
              <span data-testid="host-photo-placeholder" className="w-24 h-24 rounded-full bg-slate-100 border border-slate-200 grid place-items-center font-heading text-3xl font-bold text-slate-400">
                {(name || "?").charAt(0).toUpperCase()}
              </span>
            )}
            {photoUploading && <span className="absolute inset-0 grid place-items-center bg-white/70 rounded-full"><Loader2 size={22} className="animate-spin text-blue-600" /></span>}
          </div>
          <div>
            <button onClick={() => photoInput.current?.click()} disabled={photoUploading} data-testid="host-photo-upload" className="rounded-full bg-blue-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
              {photo ? "Change photo" : "Upload photo"}
            </button>
            <p className="text-xs text-slate-400 mt-2">JPEG / PNG / WebP · up to 12 MB</p>
            <input ref={photoInput} type="file" accept="image/*" className="hidden" data-testid="host-photo-input"
              onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </div>
      </div>

      {/* Account & login */}
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-blue-600" /> Account & login</h3>
        <p className="text-sm text-slate-500 mb-5">Update your username, login email, and password.</p>
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700">Username</label>
            <div className="flex gap-2 mt-1.5">
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="host-name-input" className="belo-input w-full" />
              <button onClick={saveName} disabled={saving === "name"} data-testid="host-name-save" className="rounded-full bg-slate-900 text-white px-5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">Save</button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="host-email-input" className="belo-input w-full mt-1.5" />
            <div className="flex gap-2 mt-2">
              <input type="password" value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} data-testid="host-email-pwd" placeholder="Confirm current password" className="belo-input w-full" />
              <button onClick={saveEmail} disabled={saving === "email"} data-testid="host-email-save" className="rounded-full bg-slate-900 text-white px-5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">Update email</button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Change password</label>
            <input type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} data-testid="host-cur-pwd" placeholder="Current password" className="belo-input w-full mt-1.5" />
            <div className="flex gap-2 mt-2">
              <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="host-new-pwd" placeholder="New password (min 6 chars)" className="belo-input w-full" />
              <button onClick={savePwd} disabled={saving === "pwd"} data-testid="host-pwd-save" className="rounded-full bg-slate-900 text-white px-5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">Update</button>
            </div>
          </div>
        </div>
      </div>

      {/* Host type */}
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <h3 className="font-heading text-lg font-semibold mb-5">Host type</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <TypeCard value="individual" icon={User} title="Individual" desc="Host under your own name." />
          <TypeCard value="business" icon={Building2} title="Business" desc="Host under a company name." />
        </div>
        {s.host_type === "business" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div>
              <label className="text-sm font-medium text-slate-700">Business name</label>
              <input value={s.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="business-name" className="belo-input w-full mt-1.5" placeholder="Bay State Rentals LLC" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">EIN <span className="text-slate-400 font-normal">(optional)</span></label>
              <input value={s.ein || ""} onChange={(e) => set("ein", e.target.value)} data-testid="business-ein" className="belo-input w-full mt-1.5" placeholder="12-3456789" />
            </div>
          </div>
        )}
        <div className="mt-6">
          <label className="text-sm font-medium text-slate-700">Contact phone</label>
          <input value={s.host_phone || ""} onChange={(e) => set("host_phone", e.target.value)} data-testid="host-phone" className="belo-input w-full mt-1.5" />
        </div>
        <button onClick={saveType} disabled={saving === "type"} data-testid="host-settings-save" className="mt-5 rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">Save</button>
      </div>

      {/* ACH payout */}
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><Landmark size={18} className="text-blue-600" /> Payout bank details (ACH)</h3>
        <p className="text-sm text-slate-500 mb-5">Where we send your payouts. You keep 100% of the rental, delivery, and young-driver fees.</p>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Account holder name</label>
            <input value={ach.holder} onChange={(e) => setAch((p) => ({ ...p, holder: e.target.value }))} data-testid="ach-holder" className="belo-input w-full mt-1.5" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Routing number</label>
              <input value={ach.routing} onChange={(e) => setAch((p) => ({ ...p, routing: e.target.value }))} data-testid="ach-routing" className="belo-input w-full mt-1.5" placeholder={s.bank_routing_last4 ? `•••••${s.bank_routing_last4}` : "9 digits"} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Account number</label>
              <input value={ach.account} onChange={(e) => setAch((p) => ({ ...p, account: e.target.value }))} data-testid="ach-account" className="belo-input w-full mt-1.5" placeholder={s.bank_account_last4 ? `••••${s.bank_account_last4}` : "Account number"} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Account type</label>
            <select value={ach.type} onChange={(e) => setAch((p) => ({ ...p, type: e.target.value }))} data-testid="ach-type" className="belo-input w-full mt-1.5">
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <button onClick={saveAch} disabled={saving === "ach"} data-testid="ach-save" className="rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">Save payout details</button>
        </div>
      </div>

      <HostVerificationCard />

      {/* Logan Airport pickup & return */}
      <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="host-airport-card">
        <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><Plane size={18} className="text-blue-600" /> Logan Airport pickup &amp; return</h3>
        <p className="text-sm text-slate-500 mb-5">Opt in to offer pickup &amp; return at Boston Logan (BOS). You'll be featured on our Logan Airport page, and guests can add the service to trips on your vehicles. You keep 100% of the airport fee.</p>
        <label data-testid="airport-optin" className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${s.offers_airport_service ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
          <input type="checkbox" checked={!!s.offers_airport_service} onChange={(e) => set("offers_airport_service", e.target.checked)} data-testid="airport-optin-checkbox" className="mt-0.5 w-5 h-5 accent-blue-600" />
          <span>
            <span className="font-semibold text-slate-900 text-sm">I offer Logan Airport pickup &amp; return</span>
            <span className="block text-slate-500 text-xs mt-0.5">Meet guests at BOS for a smooth handoff on your listed vehicles.</span>
          </span>
        </label>
        {s.offers_airport_service && (
          <div className="mt-5 max-w-xs" data-testid="airport-fee-field">
            <label className="text-sm font-medium text-slate-700">Your airport fee (per trip)</label>
            <div className="relative mt-1.5">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input type="number" min="0" step="5" value={s.airport_fee ?? ""} onChange={(e) => set("airport_fee", e.target.value)} data-testid="airport-fee-input" className="belo-input w-full pl-7" placeholder="100" />
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Added to the guest's total when they choose airport pickup &amp; return.</p>
          </div>
        )}
        <button onClick={saveAirport} disabled={saving === "airport"} data-testid="airport-save" className="mt-5 rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">Save airport settings</button>
      </div>
    </div>
  );
}
